import React, { forwardRef } from 'react';

import { Box, Divider, Flex, Tooltip } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { showOKRDetails } from 'components/RabbitOKRSidePanel/okrPanelControlSlice';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { themeColors } from 'themes/clients/baseTheme';

import {
  NodeExpandArrow,
  PrivateObjective,
  AddKr,
  AddObjective,
  ObjectiveIcon,
  KeyResultIcon,
  KeyResultPrivate2,
} from 'assets/assetsV1/iconComponents';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { minifyNumber } from 'constants/numberMinifyHelper';
import theme from 'constants/theme';

import CircularOutlineIcon from 'uiToolkit/CircularOutlineIcon';
import SimpleAvatarGroup from 'uiToolkit/SimpleAvatarGroup';
import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

const CardWrapper = styled.div`
  width: 300px;
  ${(props) =>
    props.showGoalsSection &&
    `
    width: 301px;
    border: 1px solid ${(props) => props.theme.colors.neutral.EBEFF3};
  `};
  border: 1px solid ${(props) => props.theme.colors.neutral.EBEFF3};
  border-radius: 12px;
  position: relative;
`;

const ObjectiveWrapper = styled.div`
  width: 300px;
  background: ${(props) => props.theme.colors.surface.FAFAFA};
  border-radius: 12px;
  border: 1px solid ${(props) => props.theme.colors.neutral.EBEFF3};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.25);
  cursor: ${(props) => (props.showPointer ? 'pointer' : 'inherit')};
`;

const GoalsListWrapper = styled.div`
  height: 104px;
  width: 300px;
  background: ${(props) => props.theme.colors.surface.FAFAFA};

  border: 1px solid ${(props) => props.theme.colors.neutral.EBEFF3};
  border-radius: 4px;

  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.25);
  overflow: hidden;
`;

const OptionsWrapper = styled.div`
  position: absolute;
  right: -1px;
  bottom: -32px;
`;

const GoalsExpandButtonWrapper = styled.div`
  position: absolute;
  right: -26px;
  bottom: 12px;
`;

const ObjectiveGoalMembersWrapper = styled.div`
  position: absolute;
  left: -1px;
  bottom: -32px;
`;

const ExpandCollapseWrapper = styled.div`
  position: absolute;
  right: -30px;
  top: calc(50% - 12px);
`;

const AggregatePercentWrapper = styled.div`
  width: 79px;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${(props) => props.statusColor};
  border-radius: 4px;
  background: ${(props) => `${props.statusColor}30`};
  margin-right: -1px;
  margin-top: -1px;
`;

const StyledTextBodyMedium = styled(TextBodyMedium)`
  max-width: 30ch;
  white-space: nowrap;
  & > span {
    white-space: nowrap !important;
  }
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RefbleStyledTextBodyMedium = forwardRef(({ children, ...rest }, ref) => {
  <StyledTextBodyMedium ref={ref} {...rest}>
    {children}
  </StyledTextBodyMedium>;
});

const ExpandCollapseButton = ({ isExpanded, onClick }) => {
  const Icon = () => (
    <NodeExpandArrow
      width="24px"
      height="24px"
      style={isExpanded ? { transform: 'rotate(180deg)' } : {}}
      onClick={() => onClick(!isExpanded)}
    />
  );
  return isExpanded ? <CircularOutlineIcon onClick={() => onClick(!isExpanded)} icon={Icon} /> : <Icon />;
};

const ObjectiveNode = ({
  objective,
  isExpanded = false,
  avgProgressPercentage,
  onExpandClick,
  hasChildNodes = false,
  statusColor = theme.palette.primary.main,
  type,
  onGoalObjectiveCreationClick,
  onGoalCreationClick,
  enableObjectiveCreation = false,
  toIds,
  onDelete = { onDelete },
  onUpdate = { onUpdate },
  enableKeyResultCreation = false,
}) => {
  const isPrivateKR = type !== OKR_TYPES.OBJECTIVE && objective.visibilityType === 'PRIVATE';
  const isPrivateObjective = type === OKR_TYPES.OBJECTIVE && objective.visibilityType === 'PRIVATE';
  const history = useHistory();

  const dispatch = useDispatch();
  const handleCreateObjectiveClick = (e) => {
    e.stopPropagation();
    onGoalObjectiveCreationClick();
  };

  const handleCreateGoalClick = (e) => {
    e.stopPropagation();
    onGoalCreationClick();
  };

  const withStopPropagation = (e) => {
    e.stopPropagation();
    enableObjectiveCreation && onGoalObjectiveCreationClick();
    !enableObjectiveCreation && onGoalCreationClick();
  };

  const handleObjectiveNameClick = (objective) => {
    dispatch(
      showOKRDetails({
        id: objective.uuid,
        type: objective.type,
        onDelete,
        onUpdate,
        history,
      })
    );
  };

  return (
    <CardWrapper>
      <ObjectiveWrapper statusColor={statusColor}>
        <Flex alignItems="flex-start" padding="16px">
          {type === OKR_TYPES.OBJECTIVE ? (
            isPrivateObjective ? (
              <PrivateObjective
                style={{ flex: '0 0 auto', marginTop: '4px' }}
                color={themeColors.OKR_TYPES.OBJECTIVE}
              />
            ) : (
              <ObjectiveIcon style={{ flex: '0 0 auto', marginTop: '4px' }} color={themeColors.OKR_TYPES.OBJECTIVE} />
            )
          ) : isPrivateKR ? (
            <KeyResultPrivate2
              style={{ flex: '0 0 auto', marginTop: '4px' }}
              color={themeColors.OKR_TYPES.KEY_RESULT}
            />
          ) : (
            <KeyResultIcon style={{ flex: '0 0 auto', marginTop: '4px' }} color={themeColors.OKR_TYPES.KEY_RESULT} />
          )}

          <Box marginLeft="12px" flex="1 1 auto" height="55px">
            <Tooltip label={objective.displayName}>
              <Box
                as="button"
                onClick={() => {
                  handleObjectiveNameClick(objective);
                }}
              >
                <StyledTextBodyMedium style={{ display: 'inline-block' }}>{objective.displayName}</StyledTextBodyMedium>
              </Box>
            </Tooltip>
            <Flex alignItems="center" justifyContent="space-between" marginTop="12px">
              <Box>
                <SimpleAvatarGroup employees={toIds} totalEmployeesCount={toIds.length} maxAvatarsToShow={3} />
              </Box>
              <TextBodyMedium color={statusColor} bold>
                {avgProgressPercentage ? minifyNumber(avgProgressPercentage) : 0}%
              </TextBodyMedium>
            </Flex>
          </Box>
        </Flex>

        <Divider />
        <Flex justifyContent="flex-end">
          {enableObjectiveCreation && (
            <Button onClick={handleCreateObjectiveClick} variant="tertiary" StartIcon={AddObjective} />
          )}
          {enableKeyResultCreation && <Button onClick={handleCreateGoalClick} variant="tertiary" StartIcon={AddKr} />}
        </Flex>

        {hasChildNodes && (
          <ExpandCollapseWrapper>
            <ExpandCollapseButton isExpanded={isExpanded} onClick={onExpandClick} />
          </ExpandCollapseWrapper>
        )}
      </ObjectiveWrapper>
    </CardWrapper>
  );
};

export default ObjectiveNode;
